import moment from 'moment';
import {
  ConfigBase,
  FieldValueType,
  GeneralInformationValueType,
  ItemValueType,
  LocationFormValueType,
  ReportConfig,
  ReportValue,
  UseOfForceConfigType,
  UseOfForceValueType
} from '../../../../../Models';
import {
  checkFutureDay,
  checkFutureTime,
  checkSectionsFields,
  getGeneralFormDefaultValue,
  isDurationValid
} from '../../../../../Helpers';
import {
  cloneObject,
  isFieldEmpty,
  LocationAreaType
} from 'ui-smartforce-settings';
import { isValidYear } from '../ContactForm/ContactForm';

export const getDateTime = (
  date: moment.Moment,
  time: moment.Moment
): string => {
  const dateWithTime: moment.Moment = moment(date);
  dateWithTime.set({
    hour: time ? time.get('hour') : 0,
    minutes: time ? time.get('minutes') : 0,
    second: 0,
    millisecond: 0
  });
  return dateWithTime.toISOString();
};

export const isListValid = (config: ConfigBase, list?: ItemValueType[]) => {
  if (!list || list.length === 0) {
    return true;
  }

  for (let item of list) {
    if (!checkSectionsFields(config, item)) {
      return false;
    }
  }

  return true;
};

export const isUseOfForceValid = (
  config: UseOfForceConfigType | undefined,
  useOfForceValue: UseOfForceValueType,
  isAmend?: boolean
): boolean => {
  if (!config) {
    return true;
  }

  const { other_agencies_involved, ...useOfForceFields } = useOfForceValue;

  return (
    checkSectionsFields(config, {
      use_of_force: { ...useOfForceFields }
    } as ItemValueType) &&
    (!isAmend ||
      !config.other_agencies_involved ||
      isListValid(config.other_agencies_involved, other_agencies_involved))
  );
};

export const isUseOfForceEmpty = (
  useOfForceValue: UseOfForceValueType | undefined
): boolean => {
  if (!useOfForceValue) {
    return true;
  }

  const { other_agencies_involved, ...useOfForceFields } = useOfForceValue;

  return (
    other_agencies_involved.length === 0 &&
    Object.keys(useOfForceFields).length === 0
  );
};

export const isLocationFormValid = (value: LocationFormValueType): boolean => {
  return (
    !isFieldEmpty(value.address?.full) &&
    !isFieldEmpty(value.type) &&
    !isFieldEmpty(value.address.city) &&
    !isFieldEmpty(value.address.zip)
  );
};

export const isGeneralInformationValid = (
  value: GeneralInformationValueType,
  config: ConfigBase
): boolean => {
  return (
    !isFieldEmpty(value.type as string) &&
    !isFieldEmpty(value.date as FieldValueType) &&
    (value.date as moment.Moment).isValid() &&
    isValidYear(value.date as moment.Moment) &&
    !isFieldEmpty(value.time as FieldValueType) &&
    (value.time as moment.Moment).isValid() &&
    !checkFutureDay(value.date as moment.Moment) &&
    !checkFutureTime(
      value.date as moment.Moment,
      value.time as moment.Moment
    ) &&
    !isFieldEmpty(value.duration as FieldValueType) &&
    isDurationValid(value.duration as string) &&
    isLocationFormValid(value.location) &&
    checkSectionsFields(config, value.state_related)
  );
};

export const checkFormValid = (
  report: ReportValue,
  config: ReportConfig,
  isAmend?: boolean
): boolean => {
  return (
    report.citizens.length > 0 &&
    isGeneralInformationValid(
      report.general_information,
      config.general_information
    ) &&
    (!report.general_information.was_use_of_force ||
      isUseOfForceValid(
        config.use_of_force,
        report.use_of_force as UseOfForceValueType,
        isAmend
      )) &&
    (!isAmend ||
      (isListValid(config.citizen, report.citizens) &&
        isListValid(config.officer, report.officers) &&
        isListValid(config.complaint, report.complaints) &&
        isListValid(config.investigation, report.investigations) &&
        isListValid(config.administrative, report.administrative)))
  );
};

export const initGeneralInfoFromReport = (
  report: ReportValue
): GeneralInformationValueType => ({
  ...cloneObject<GeneralInformationValueType>(report.general_information),
  date: report.general_information.date
    ? moment(report.general_information.date as string)
    : moment(),
  time: report.general_information.date
    ? moment(report.general_information.date as string)
    : moment()
});

export const getGeneralFormInitialValue = (
  config: ConfigBase,
  caseNumber?: string,
  areas?: LocationAreaType[]
): GeneralInformationValueType => {
  const form: GeneralInformationValueType = getGeneralFormDefaultValue(config);
  if (caseNumber) {
    form.state_related = {
      to_connect_reports: {
        event_identifier: caseNumber
      }
    };
  }

  return {
    ...form,
    location: {
      ...form.location,
      areas: areas
    }
  };
};
